/**
 * VerifyComplianceSignaturePage — Vérification publique QR rapport conformité (Iter386 Lot B)
 *
 * Page publique (sans auth) atteinte via le QR code embarqué dans le PDF.
 * URL : /verify-compliance/:campaignId?sig=<sha256>
 * Backend : GET /api/public/compliance/verify?cid=&sig=
 */
import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { ShieldCheck, XCircle, Loader2, ArrowLeft } from 'lucide-react';
import { getApiUrl } from '../config/environment';

const API = getApiUrl();

export default function VerifyComplianceSignaturePage() {
  const { campaignId } = useParams();
  const [params] = useSearchParams();
  const sig = params.get('sig') || '';
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    if (!campaignId || !sig) {
      setState({ loading: false, data: null, error: 'invalid_format' });
      return;
    }
    (async () => {
      try {
        const r = await fetch(`${API}/public/compliance/verify?cid=${encodeURIComponent(campaignId)}&sig=${encodeURIComponent(sig)}`);
        if (r.ok) {
          const data = await r.json();
          setState({ loading: false, data, error: null });
        } else if (r.status === 404) {
          setState({ loading: false, data: null, error: 'not_found' });
        } else if (r.status === 400) {
          setState({ loading: false, data: null, error: 'invalid_format' });
        } else {
          setState({ loading: false, data: null, error: 'service_unavailable' });
        }
      } catch {
        setState({ loading: false, data: null, error: 'network_error' });
      }
    })();
  }, [campaignId, sig]);

  return (
    <div className="min-h-screen bg-[#0F0E0C] text-[#F3E9C8] flex items-center justify-center p-6" data-testid="verify-compliance-page">
      <div className="max-w-xl w-full">
        <div className="flex items-center gap-2 mb-8">
          <span className="text-[10px] uppercase tracking-[0.18em] text-[#C49B2A] font-semibold">
            COACHDIA · Audit
          </span>
        </div>

        {state.loading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 text-[#C49B2A] animate-spin" />
          </div>
        )}

        {!state.loading && state.data && state.data.authentic && (
          <div className="bg-[#FBF9F3] text-[#1F1A12] rounded-2xl p-8 border border-[#C49B2A]" data-testid="verify-compliance-authentic">
            <div className="flex items-center gap-3 mb-6">
              <ShieldCheck className="w-8 h-8 text-[#92710A]" />
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-[#92710A] font-semibold">Rapport authentique</div>
                <h1 className="text-2xl font-light text-[#1F1A12]" style={{ fontFamily: 'Georgia, serif' }}>
                  Conformité certifiée
                </h1>
              </div>
            </div>

            <dl className="space-y-3 text-sm">
              <div className="flex justify-between gap-4 border-b border-[#E8E2D2] pb-2">
                <dt className="text-[#6B5E48]">Entreprise</dt>
                <dd className="text-[#1F1A12] font-medium text-right">{state.data.company || '—'}</dd>
              </div>
              <div className="flex justify-between gap-4 border-b border-[#E8E2D2] pb-2">
                <dt className="text-[#6B5E48]">Période</dt>
                <dd className="text-[#1F1A12] font-medium text-right">
                  {state.data.period_start} → {state.data.period_end}
                </dd>
              </div>
              <div className="flex justify-between gap-4 border-b border-[#E8E2D2] pb-2">
                <dt className="text-[#6B5E48]">Émis le</dt>
                <dd className="text-[#1F1A12] font-medium text-right">
                  {state.data.generated_at ? new Date(state.data.generated_at).toLocaleString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                </dd>
              </div>
              {state.data.kpi && (
                <>
                  <div className="flex justify-between gap-4 border-b border-[#E8E2D2] pb-2">
                    <dt className="text-[#6B5E48]">Heures certifiées</dt>
                    <dd className="text-[#1F1A12] font-semibold tabular-nums text-right">
                      {Number(state.data.kpi.certified_hours || 0).toFixed(1).replace('.', ',')} h
                    </dd>
                  </div>
                  <div className="flex justify-between gap-4 border-b border-[#E8E2D2] pb-2">
                    <dt className="text-[#6B5E48]">Sessions certifiées</dt>
                    <dd className="text-[#1F1A12] font-semibold tabular-nums text-right">
                      {state.data.kpi.certified_sessions} / {state.data.kpi.total_sessions}
                    </dd>
                  </div>
                  <div className="flex justify-between gap-4 border-b border-[#E8E2D2] pb-2">
                    <dt className="text-[#6B5E48]">Score conformité</dt>
                    <dd className="text-[#1F1A12] font-semibold tabular-nums text-right">{state.data.kpi.compliance_score}/100</dd>
                  </div>
                </>
              )}
              <div className="flex justify-between gap-4">
                <dt className="text-[#6B5E48]">Empreinte SHA-256</dt>
                <dd className="text-[#92710A] font-mono text-xs text-right">{state.data.signature_short}…</dd>
              </div>
            </dl>

            <p className="text-[11px] text-[#A89F8A] mt-6">
              Émis par {state.data.issuer || 'COACHDIA'}. Document signé cryptographiquement (SHA-256).
            </p>
          </div>
        )}

        {!state.loading && state.error === 'not_found' && (
          <div className="bg-[#1A0E0E] border border-[#5C1F1F] rounded-2xl p-8 text-[#F5C7C7]" data-testid="verify-compliance-not-found">
            <XCircle className="w-7 h-7 text-[#E89090] mb-3" />
            <h1 className="text-xl font-light text-[#F5C7C7] mb-2" style={{ fontFamily: 'Georgia, serif' }}>
              Authenticité non vérifiée
            </h1>
            <p className="text-sm text-[#E0BABA]">
              Ce document ne correspond à aucune campagne enregistrée.
              La signature ou l'identifiant est invalide.
            </p>
          </div>
        )}

        {!state.loading && state.error === 'invalid_format' && (
          <div className="bg-[#1A0E0E] border border-[#5C1F1F] rounded-2xl p-8 text-[#F5C7C7]" data-testid="verify-compliance-invalid">
            <XCircle className="w-7 h-7 text-[#E89090] mb-3" />
            <h1 className="text-xl font-light text-[#F5C7C7] mb-2" style={{ fontFamily: 'Georgia, serif' }}>
              Lien invalide
            </h1>
            <p className="text-sm text-[#E0BABA]">
              Le format du lien de vérification n'est pas reconnu. Scanne à nouveau le QR depuis le PDF.
            </p>
          </div>
        )}

        {!state.loading && (state.error === 'service_unavailable' || state.error === 'network_error') && (
          <div className="bg-[#1F1810] border border-[#3F3520] rounded-2xl p-8 text-[#EAD18E]" data-testid="verify-compliance-error">
            <Loader2 className="w-7 h-7 text-[#EAD18E] mb-3" />
            <h1 className="text-xl font-light text-[#EAD18E] mb-2" style={{ fontFamily: 'Georgia, serif' }}>
              Service indisponible
            </h1>
            <p className="text-sm text-[#D6BB73]">Réessaye dans quelques instants.</p>
          </div>
        )}

        <div className="mt-8 text-center">
          <Link to="/" className="inline-flex items-center gap-1.5 text-xs text-[#A89F8A] hover:text-[#F3E9C8] transition-colors" data-testid="verify-compliance-home">
            <ArrowLeft className="w-3 h-3" /> Retour COACHDIA
          </Link>
        </div>
      </div>
    </div>
  );
}
